import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import apiClient, { API_BASE_URL, uploadFormData } from '../utils/apiClient';
import './OralHistoryManagement.css';

const emptyArticle = {
  id: null,
  slug: '',
  seriesName: '馆藏心声',
  seriesNumber: '',
  title: '',
  author: '',
  editor: '业羊',
  publishedDate: '',
  excerpt: '',
  contentMarkdown: '',
  coverImage: '',
  originalUrl: '',
  referenceType: '',
  referenceId: '',
  referenceLabel: ''
};

const mediaUrl = (value) => {
  if (!value || /^https?:\/\//i.test(value)) return value;
  return `${API_BASE_URL}${value}`;
};

const fromApi = (article) => ({
  id: article.id,
  slug: article.slug || '',
  seriesName: article.series_name || '馆藏心声',
  seriesNumber: article.series_number ?? '',
  title: article.title || '',
  author: article.author || '',
  editor: article.editor || '业羊',
  publishedDate: article.published_date || '',
  excerpt: article.excerpt || '',
  contentMarkdown: article.content_markdown || '',
  coverImage: article.cover_image || '',
  originalUrl: article.original_url || '',
  referenceType: article.reference_type || '',
  referenceId: article.reference_id || '',
  referenceLabel: article.reference_label || ''
});

const OralHistoryManagement = ({ games = [], platforms = [] }) => {
  const [articles, setArticles] = useState([]);
  const [form, setForm] = useState(emptyArticle);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(true);
  const [imageAlt, setImageAlt] = useState('文章配图');
  const markdownRef = useRef(null);

  const loadArticles = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await apiClient.get('/oral-history-admin/articles');
      setArticles(data);
    } catch (requestError) {
      setError(requestError.response?.data?.error || '无法载入口述史文章。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadArticles(); }, []);

  const sortedGames = useMemo(() => [...games].sort((left, right) => (
    (left.titlechn || left.titleeng || '').localeCompare(right.titlechn || right.titleeng || '', 'zh-CN')
  )), [games]);
  const sortedPlatforms = useMemo(() => [...platforms].sort((left, right) => (
    (left.namechn || left.nameeng || '').localeCompare(right.namechn || right.nameeng || '', 'zh-CN')
  )), [platforms]);

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const startNew = () => {
    setForm({ ...emptyArticle });
    setMessage('');
    setError('');
    window.requestAnimationFrame(() => document.querySelector('.oral-manage-editor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const editArticle = (article) => {
    setForm(fromApi(article));
    setMessage('');
    setError('');
    window.requestAnimationFrame(() => document.querySelector('.oral-manage-editor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const insertMarkdown = (before, after = before, placeholder = '文本') => {
    const textarea = markdownRef.current;
    const start = textarea?.selectionStart ?? form.contentMarkdown.length;
    const end = textarea?.selectionEnd ?? start;
    const selected = form.contentMarkdown.slice(start, end) || placeholder;
    const next = `${form.contentMarkdown.slice(0, start)}${before}${selected}${after}${form.contentMarkdown.slice(end)}`;
    update('contentMarkdown', next);
    window.requestAnimationFrame(() => {
      textarea?.focus();
      const cursor = start + before.length + selected.length + after.length;
      textarea?.setSelectionRange(cursor, cursor);
    });
  };

  const insertUploadedMarkdown = (markdown) => {
    const textarea = markdownRef.current;
    const start = textarea?.selectionStart ?? form.contentMarkdown.length;
    const prefix = start > 0 && !form.contentMarkdown.slice(0, start).endsWith('\n\n') ? '\n\n' : '';
    const suffix = form.contentMarkdown.slice(start).startsWith('\n\n') ? '' : '\n\n';
    const insertion = `${prefix}${markdown}${suffix}`;
    update('contentMarkdown', `${form.contentMarkdown.slice(0, start)}${insertion}${form.contentMarkdown.slice(start)}`);
  };

  const uploadImage = async (file, purpose) => {
    if (!file) return;
    if (!form.slug) {
      setError('上传图片前请先填写链接标识。');
      return;
    }
    setUploading(purpose);
    setError('');
    try {
      const data = new FormData();
      data.append('slug', form.slug);
      data.append('alt', imageAlt || '文章配图');
      data.append('image', file);
      const response = await uploadFormData('/oral-history-admin/images', data);
      if (purpose === 'cover') update('coverImage', response.url);
      else insertUploadedMarkdown(response.markdown);
      setMessage(purpose === 'cover' ? '封面已上传。' : '图片已上传并插入正文。');
    } catch (uploadError) {
      setError(uploadError.message || '图片上传失败。');
    } finally {
      setUploading('');
    }
  };

  const saveArticle = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = { ...form };
      const response = form.id
        ? await apiClient.put(`/oral-history-admin/articles/${form.id}`, payload)
        : await apiClient.post('/oral-history-admin/articles', payload);
      setMessage(form.id ? '文章已更新。' : '文章已创建。');
      await loadArticles();
      if (!form.id) {
        const created = (await apiClient.get('/oral-history-admin/articles')).data.find((item) => item.id === response.data.id);
        if (created) setForm(fromApi(created));
      }
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || '文章保存失败。');
    } finally {
      setSaving(false);
    }
  };

  const deleteArticle = async (article) => {
    if (!window.confirm(`确定删除「${article.title}」吗？正文、评论、投票和文章图片都会被删除。`)) return;
    setError('');
    setMessage('');
    try {
      await apiClient.delete(`/oral-history-admin/articles/${article.id}`);
      if (form.id === article.id) setForm({ ...emptyArticle });
      setMessage('文章已删除。');
      await loadArticles();
    } catch (requestError) {
      setError(requestError.response?.data?.error || '文章删除失败。');
    }
  };

  const referenceItems = form.referenceType === 'game' ? sortedGames : form.referenceType === 'platform' ? sortedPlatforms : [];
  const referenceImage = form.referenceType === 'game'
    ? games.find((game) => String(game.id) === String(form.referenceId))?.cover
    : platforms.find((platform) => String(platform.id) === String(form.referenceId))?.img;

  const previewComponents = useMemo(() => ({
    img: ({ src, alt }) => <img src={mediaUrl(src)} alt={alt || ''} />,
    a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
  }), []);

  return (
    <section className="oral-manage">
      <div className="oral-manage-heading">
        <div><h2>游戏口述史</h2><p>新建、编辑或删除文章，并管理 Markdown、图片与页尾入口。</p></div>
        <button type="button" className="oral-manage-primary" onClick={startNew}>新建文章</button>
      </div>

      {message && <div className="oral-manage-notice success">{message}</div>}
      {error && <div className="oral-manage-notice error">{error}</div>}

      <div className="oral-manage-list">
        {loading ? <p>正在载入…</p> : articles.map((article) => (
          <article key={article.id} className={form.id === article.id ? 'active' : ''}>
            {article.cover_image && <img src={mediaUrl(article.cover_image)} alt="" />}
            <div><span>{article.series_name}</span><h3>{article.title}</h3><p>{article.author || '未填写作者'} · {article.published_date || '未填写日期'}</p></div>
            <div className="oral-manage-list-actions">
              <a href={`/oral-history/${article.slug}`} target="_blank" rel="noopener noreferrer">查看</a>
              <button type="button" onClick={() => editArticle(article)}>编辑</button>
              <button type="button" className="danger" onClick={() => deleteArticle(article)}>删除</button>
            </div>
          </article>
        ))}
      </div>

      <form className="oral-manage-editor" onSubmit={saveArticle}>
        <div className="oral-manage-editor-title">
          <h2>{form.id ? `编辑 #${form.id}` : '新建文章'}</h2>
          {form.id && <button type="button" onClick={startNew}>取消编辑</button>}
        </div>

        <div className="oral-manage-fields two-columns">
          <label><span>系列名称 *</span><select value={form.seriesName} onChange={(event) => update('seriesName', event.target.value)}><option value="馆藏心声">馆藏心声</option><option value="游戏会客厅">游戏会客厅</option></select></label>
          <label><span>系列期数</span><input type="number" min="0" value={form.seriesNumber} onChange={(event) => update('seriesNumber', event.target.value)} placeholder="4" /></label>
          <label className="wide"><span>完整标题 *（包含馆藏心声或游戏会客厅）</span><input value={form.title} onChange={(event) => update('title', event.target.value)} placeholder="馆藏心声 | Vol.5 《游戏名》" required /></label>
          <label><span>链接标识 *</span><input value={form.slug} onChange={(event) => update('slug', event.target.value.toLowerCase().replace(/\s+/g, '-'))} placeholder="article-slug" required /></label>
          <label><span>发布日期</span><input type="date" value={form.publishedDate} onChange={(event) => update('publishedDate', event.target.value)} /></label>
          <label><span>作者</span><input value={form.author} onChange={(event) => update('author', event.target.value)} /></label>
          <label><span>编辑</span><input value={form.editor} onChange={(event) => update('editor', event.target.value)} /></label>
          <label className="wide"><span>微信公众号原文 *</span><input type="url" value={form.originalUrl} onChange={(event) => update('originalUrl', event.target.value)} placeholder="https://mp.weixin.qq.com/s/..." required /></label>
          <label className="wide"><span>摘要（留空将从正文自动生成）</span><textarea rows="3" value={form.excerpt} onChange={(event) => update('excerpt', event.target.value)} /></label>
        </div>

        <fieldset className="oral-manage-media">
          <legend>封面与图片</legend>
          <div className="oral-manage-cover">
            <div className="oral-manage-cover-preview">{form.coverImage ? <img src={mediaUrl(form.coverImage)} alt="封面预览" /> : <span>暂无封面</span>}</div>
            <div>
              <label><span>封面路径 *</span><input value={form.coverImage} onChange={(event) => update('coverImage', event.target.value)} placeholder="/oral-history-media/..." required /></label>
              <label className="oral-upload-button">{uploading === 'cover' ? '上传中…' : '上传封面'}<input type="file" accept="image/jpeg,image/png,image/gif,image/webp" disabled={!!uploading} onChange={(event) => uploadImage(event.target.files?.[0], 'cover')} /></label>
            </div>
          </div>
          <div className="oral-inline-upload">
            <label><span>图片说明</span><input value={imageAlt} onChange={(event) => setImageAlt(event.target.value)} /></label>
            <label className="oral-upload-button">{uploading === 'inline' ? '上传中…' : '上传并插入正文'}<input type="file" accept="image/jpeg,image/png,image/gif,image/webp" disabled={!!uploading} onChange={(event) => uploadImage(event.target.files?.[0], 'inline')} /></label>
          </div>
        </fieldset>

        <fieldset className="oral-manage-markdown-field">
          <legend>Markdown 正文 *</legend>
          <div className="oral-markdown-toolbar">
            <button type="button" onClick={() => insertMarkdown('## ', '', '小节标题')}>H2</button>
            <button type="button" onClick={() => insertMarkdown('### ', '', '说话人：')}>H3</button>
            <button type="button" onClick={() => insertMarkdown('**', '**', '粗体')}>B</button>
            <button type="button" onClick={() => insertMarkdown('*', '*', '图片说明')}>I</button>
            <button type="button" onClick={() => insertMarkdown('> ', '', '引用')}>❝</button>
            <button type="button" onClick={() => insertMarkdown('[', '](https://)', '链接文字')}>链接</button>
            <button type="button" onClick={() => setShowPreview((current) => !current)}>{showPreview ? '隐藏预览' : '显示预览'}</button>
          </div>
          <div className={`oral-markdown-workspace ${showPreview ? '' : 'preview-hidden'}`}>
            <textarea ref={markdownRef} value={form.contentMarkdown} onChange={(event) => update('contentMarkdown', event.target.value)} required spellCheck="false" />
            {showPreview && <div className="oral-manage-preview"><ReactMarkdown remarkPlugins={[remarkGfm]} components={previewComponents}>{form.contentMarkdown || '*预览将显示在这里*'}</ReactMarkdown></div>}
          </div>
        </fieldset>

        <fieldset className="oral-manage-reference">
          <legend>页尾游戏或平台入口</legend>
          <div className="oral-manage-fields two-columns">
            <label><span>入口类型</span><select value={form.referenceType} onChange={(event) => setForm((current) => ({ ...current, referenceType: event.target.value, referenceId: '', referenceLabel: '' }))}><option value="">无</option><option value="game">游戏</option><option value="platform">平台展区</option></select></label>
            {form.referenceType && <label><span>选择{form.referenceType === 'game' ? '游戏' : '平台'}</span><select value={form.referenceId} onChange={(event) => update('referenceId', event.target.value)} required><option value="">请选择</option>{referenceItems.map((item) => <option key={item.id} value={item.id}>{form.referenceType === 'game' ? `${item.titlechn || item.titleeng} ${item.titleeng && item.titlechn ? `(${item.titleeng})` : ''}` : `${item.namechn || item.nameeng} ${item.nameeng && item.namechn ? `(${item.nameeng})` : ''}`}</option>)}</select></label>}
            {form.referenceType && <label className="wide"><span>入口标签（可选）</span><input value={form.referenceLabel} onChange={(event) => update('referenceLabel', event.target.value)} placeholder={form.referenceType === 'game' ? '游戏名称' : '平台展区'} /></label>}
          </div>
          {referenceImage && <div className="oral-reference-preview"><img src={mediaUrl(referenceImage)} alt="" /><span>将在文章页尾显示此入口</span></div>}
        </fieldset>

        <div className="oral-manage-save">
          <button type="submit" className="oral-manage-primary" disabled={saving || !!uploading}>{saving ? '保存中…' : form.id ? '保存修改' : '创建文章'}</button>
        </div>
      </form>
    </section>
  );
};

export default OralHistoryManagement;
